<template>
  <div class="product-usages-details">
    <table class="product-usages-details__table">
      <caption class="product-usages-details__title">
        <span class="product-usages-details__title_stage">{{ i18n._("Stade d'application BBCH&nbsp;:") }}</span>
        <span class="product-usages-details__title_description" data-test-usage-bbch>{{ ` ${bbchLabel}` }}</span>
      </caption>
      <p-product-usage-details-row
        v-for="attribute in usageAttributes"
        :key="attribute.title"
        :title="attribute.title"
        :value="attribute.value"
      />
    </table>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'
import { i18n } from '@b2ag/locale'
import PProductUsageDetailsRow from '../PProductUsageDetailsRow/PProductUsageDetailsRow.vue'
import type { Usage } from '@b2ag/module-product-sdk'

export default defineComponent({
  name: 'PProductUsageDetails',
  components: { PProductUsageDetailsRow },
  props: {
    usage: {
      type: Object as PropType<Usage>,
      default: () => ({}),
    },
  },
  setup(props) {
    const { usage } = props

    const bbchLabel = computed(() => {
      if (usage.bbch_start && usage.bbch_end) {
        return `${i18n._('De')} ${usage.bbch_start} ${i18n._('à')} ${usage.bbch_end}`
      }
      if (usage.bbch_start) {
        return `${i18n._('À partir de')} ${usage.bbch_start}`
      }
      if (usage.bbch_end) {
        return `${i18n._("Jusqu'à")} ${usage.bbch_end}`
      }
      return i18n._('Tous')
    })

    const mapUsageRestriction = (restriction: any) => {
      return `${i18n._('Usage')} ${restriction.restriction_type} - ${restriction.perimeter} - ${restriction.label} - ${i18n._('Dosage:')} ${restriction.dose}`
    }

    const usesRestrictions = computed(() => {
      const usesRestrictionsLength = usage.uses_restrictions?.length
      if (!usesRestrictionsLength || usesRestrictionsLength === 0) {
        return undefined
      }

      return [...usage.uses_restrictions!.map(mapUsageRestriction)].join('\n')
    })

    const aquaticUnsprayedBufferZone = computed(() =>
      usage.aquatic_unsprayed_buffer_zone ? `${usage.aquatic_unsprayed_buffer_zone} m` : undefined,
    )
    const usageComment = computed(() => usage.comment?.replace(/\\n/g, '\n'))
    const licensed_dose = computed(() => `${usage.value} ${usage.unit}`)

    return {
      bbchLabel,
      i18n,
      usageAttributes: [
        { title: i18n._('Type de traitement'), value: usage.type_treatment },
        { title: i18n._("État d'usage par culture"), value: usage.state_use_crops },
        { title: i18n._("Date de début d'utilisation de l'usage"), value: usage.use_start_date },
        { title: i18n._("Date de fin d'utilisation de l'usage"), value: usage.use_use_end_date },
        { title: i18n._('Dose homologuée'), value: licensed_dose.value },
        { title: i18n._("Date et période d'application"), value: usage.application_date_and_period },
        { title: i18n._('Nombre max d’applications'), value: usage.max_number_applications },
        { title: i18n._("Intervalle d'application"), value: usage.application_interval },
        { title: i18n._("Stade BBCH mini d'application"), value: usage.bbch_start },
        { title: i18n._("Stade BBCH maxi d'application"), value: usage.bbch_end },
        { title: i18n._('Stade limite BBCH'), value: usage.bbch_limit_stage },
        { title: i18n._('Délai Avant Récolte (DAR)'), value: usage.pre_harvest_interval },
        { title: i18n._('Information après DAR'), value: usage.information_after_dar },
        { title: i18n._('Délai de Ré-Entrée dans les locaux'), value: usage.dre_local },
        { title: i18n._('Délai de Ré-entrée (DRE)'), value: usage.re_entry_interval },
        { title: i18n._('ZNT Aquatique'), value: aquaticUnsprayedBufferZone.value },
        { title: i18n._('Commentaire ZNT aquatique'), value: usage.znt_aquatic_information },
        { title: i18n._('ZNT terrestre zone cultivée'), value: usage.znt_land_cultivated_area },
        { title: i18n._('ZNT terrestre zone non cultivée'), value: usage.znt_land_uncultivated_area },
        { title: i18n._('Commentaire ZNT terrestre'), value: usage.znt_land_commentary },
        { title: i18n._('ZNT Riverain'), value: usage.znt_residents },
        { title: i18n._('Commentaire distance de sécurité'), value: usage.znt_residents_commentary },
        { title: i18n._('Commentaire sol drainé'), value: usage.drained_soil_commentary },
        { title: i18n._("Intervalle d'application pour le fractionnement"), value: usage.splits_application_interval },
        { title: i18n._('Nombre mini de fractionnement'), value: usage.splits_min_number },
        { title: i18n._('Nombre maxi de fractionnement'), value: usage.splits_max_number },
        { title: i18n._('Dose maxi fractionnement'), value: usage.splits_max_dose },
        { title: i18n._('Commentaire fractionnement'), value: usage.splits_commentary },
        { title: i18n._('Matériel atténuation dérive'), value: usage.drift_attenuation_material },
        { title: i18n._('Nom de l’usage'), value: usage.name },
        { title: i18n._("Spécification d'usage"), value: usageComment.value },
        { title: i18n._('Mention abeille'), value: usage.mention_bee },
        { title: i18n._('DVP (Dispositif végétalisé permanent)'), value: usage.dvp },
        { title: i18n._('Culture attractive'), value: usage.attractive_crop },
        { title: i18n._('Délai de dérogation en jours'), value: usage.derogation_period_in_days?.toString() },
        { title: i18n._('Réduction dérive'), value: usage.derivative_reduction },
        { title: i18n._('Restrictions d’usage'), value: usesRestrictions.value },
      ],
    }
  },
})
</script>

<style lang="scss">
.product-usages-details {
  margin: 0 2rem 3.5rem;
  background-color: var(--color-white);
  color: var(--color-black);
  font-size: 1.4rem;
  line-height: 2.1rem;

  @media (min-width: $breakpoint-md) {
    margin: 2.5rem 2rem 3rem 0;
  }
}

.product-usages-details__title {
  caption-side: top;
  background-color: var(--color-background);
  padding: 1rem;
  color: var(--color-black);
  font-size: 1.4rem;
  line-height: 2.1rem;
  text-align: center;
  text-transform: uppercase;
}

.product-usages-details__title_description {
  font-weight: bold;
}

.product-usages-details__table {
  width: 100%;
  padding: 0;
  margin: 0;
}
</style>
